import { TFunction } from 'i18next'
import { kebabCase } from 'lodash'

// AOS-797 Phase 3: the legacy "first-dot is the namespace" split is incorrect
// under the tab-aligned layout — keys can land in any namespace regardless of
// their first dot-segment (e.g. `airports.all` → ns `common`). Wrapper
// resolution must therefore consult the v2 keyToNamespace map.
//
// Module-loaded JSON: rspack/metro inline it; cost is paid once per process.
// eslint-disable-next-line @typescript-eslint/no-var-requires
import namespaceMapJson from '../../../../aos-i18n/scripts/namespace-map.json'
import { AnyEnum, enumToLabel, enumToName, EnumValue, isValidEnumEntry } from '../Enum'

interface TranslatePayload {
    [key: string]: any
}

const namespaceMap = namespaceMapJson as unknown as {
    version: number
    bareKeysNamespace: string
    keyToNamespace: Record<string, string>
}
const KEY_TO_NS = namespaceMap.keyToNamespace

const splitNamespace = (flatKey: string): { ns: string; key: string } => {
    const ns = KEY_TO_NS[flatKey]
    if (!ns) {
        // Fallback (parseMissingKeyHandler path): preserve historic first-dot
        // split so unknown keys still return ns/key shapes downstream code
        // expects. The eventual `t(...)` lookup will miss in either case.
        const dot = flatKey.indexOf('.')
        if (dot < 0) {
            return { ns: flatKey, key: '' }
        }
        return { ns: flatKey.slice(0, dot), key: flatKey.slice(dot + 1) }
    }
    if (flatKey === ns) {
        return { ns, key: '' }
    }
    if (flatKey.startsWith(ns + '.')) {
        return { ns, key: flatKey.slice(ns.length + 1) }
    }
    return { ns, key: flatKey }
}

const translateFlat = (translate: TFunction, flatKey: string, payload?: TranslatePayload) => {
    const { ns, key } = splitNamespace(flatKey)
    if (!key) {
        return (translate as any)(ns, payload)
    }
    return (translate as any)(key, { ...(payload || {}), ns })
}

/**
 * Resolves a flat dot-notation key against the per-namespace i18n bundles.
 *
 * Joins the variadic items into a single dotted string, then splits on the
 * first dot to derive the namespace, so the call site can pass a
 * dynamically-computed key without knowing where the ns/key boundary lives.
 * Example: `translateParts(t)('flight-information.share.url', 'label')`
 * → `t('share.url.label', { ns: 'flight-information' })`.
 *
 * **Use this when the key (or its prefix) is genuinely dynamic per call** —
 * for example a `keyPrefix` prop on `Labeled*` form components, an entry in a
 * `PropsList` items array, or a value returned by `getStatusLabelKey()`.
 *
 * **For static, file-local prefixes, prefer `i18next.getFixedT(null, ns,
 * keyPrefix)` instead.** It's the library's native primitive, hoists the
 * prefix out of the call site, and is recognized by Tolgee CLI / i18next
 * extractors out of the box. See the dashboard `*HelpConfig.ts` files for
 * examples.
 */
export const translateParts =
    (translate: TFunction) =>
    (...items: (string | number)[]) =>
        translateFlat(translate, items.join('.'))

/**
 * Same shape as `translateParts`, but returns `''` when the key is missing
 * instead of falling through to the raw key as i18next does by default.
 *
 * **Use this for optional UI text where missing translations should render
 * empty rather than expose the key** — typically `placeholder` and `error`
 * on `Labeled*` form components. Keep `translateParts` for `label` and
 * other required text so a missing key surfaces visibly as a bug.
 */
export const translatePartsOptional =
    (translate: TFunction) =>
    (...items: (string | number)[]) => {
        const flatKey = items.join('.')
        return hasTranslation(translate)(flatKey) ? translateFlat(translate, flatKey) : ''
    }

/**
 * Field validation message for `<prefix>.error`, falling back to the generic
 * `form.required-field` message when that key is missing.
 *
 * Without this fallback, `translatePartsOptional` returns `''` for a missing
 * `<prefix>.error` key and the field renders an empty error row — the AOS-1230
 * symptom. Use this instead of calling `translatePartsOptional(...)(prefix,
 * 'error')` directly for any per-field error message.
 */
export const translateFieldError = (translate: TFunction) => (prefix: string) =>
    translatePartsOptional(translate)(prefix, 'error') ||
    translate('form.required-field', { ns: 'common' })

export const translatePlural = (translate: TFunction) => (key: string, count: number) =>
    count === 1
        ? translateFlat(translate, `${key}.single`, { count })
        : translateFlat(translate, `${key}.plural`, { count })

export const translateFirst =
    (translate: TFunction) =>
    (keys: string[], payload?: TranslatePayload): string => {
        const firstKey = keys.find(key => hasTranslation(translate)(key))
        if (firstKey) {
            return translateFlat(translate, firstKey, payload)
        }
        return keys[0]
    }

export const translateEnum =
    <E extends EnumValue, T extends AnyEnum = AnyEnum>(translate: TFunction) =>
    (enumEntity: T, prefix: string, fallbackEnumValue?: E) =>
    (enumValue: E) => {
        const enumLabel = enumToLabel(enumEntity, prefix)
        if (isValidEnumEntry(enumEntity)(enumValue)) {
            return translateFlat(translate, enumLabel(enumValue))
        }
        return translateFlat(translate, enumLabel(fallbackEnumValue!))
    }

export const translateFirstEnum =
    <E extends EnumValue, T extends AnyEnum = AnyEnum>(translate: TFunction) =>
    (enumEntity: T, prefixes: string[], fallbackEnumValue?: E) =>
    (enumValue: E) => {
        const enumLabel = enumToName(enumEntity)(enumValue)
        const firstKey = prefixes.find(key => hasTranslation(translate)(`${key}.${enumLabel}`))
        const prefix = firstKey ? firstKey : prefixes[0]
        return translateEnum<E>(translate)(enumEntity, prefix, fallbackEnumValue)(enumValue)
    }

export const translateEnumUnion =
    (translate: TFunction) =>
    <V extends EnumValue>(prefix: string, ...enumEntities: AnyEnum[]) =>
    (value: V) => {
        const enumEntity = enumEntities.find(e => isValidEnumEntry(e)(value)) || enumEntities[0]
        return translateEnum(translate)(enumEntity, prefix)(value)
    }

export const hasTranslation =
    (translate: TFunction) =>
    (flatKey: string): boolean => {
        const { ns, key } = splitNamespace(flatKey)
        if (!key) {
            return translate(ns) !== ns
        }
        return (translate as any)(key, { ns }) !== key
    }

export const translateCode =
    (translate: TFunction) =>
    (prefix: string, code: string | undefined, fallback: string): string => {
        if (!code) {
            return fallback
        }
        const flatKey = `${prefix}.${kebabCase(code)}`
        return hasTranslation(translate)(flatKey) ? translateFlat(translate, flatKey) : fallback
    }
